import { useResponseTypeClasses } from '@heidi/answer-kit';
import { Button } from '@heidi/design-system/button';
import { TypographyP2 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import {
  Check,
  ChevronDown,
  ChevronsUp,
  ChevronsUpDown,
  Circle,
  CircleDashed,
  CircleSlash,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import { agentTodoMessages } from '../../../../data/projects/timeline/agent-todo/agentTodo.messages';
import { collapsePlanSteps } from '../../../../data/projects/timeline/agent-todo/collapsePlanSteps';
import type {
  AgentTodoItem,
  AgentTodoItemStatus,
} from '../../../../data/projects/timeline/agent-todo/types';

const STATUS_INDICATORS: ReadonlyArray<[AgentTodoItemStatus, LucideIcon, string]> = [
  ['pending', Circle, 'text-text-tertiary'],
  ['in_progress', CircleDashed, 'text-text-primary'],
  ['completed', Check, 'text-text-secondary'],
  ['cancelled', CircleSlash, 'text-text-tertiary'],
];

const LABEL_CLASS: Record<AgentTodoItemStatus, string> = {
  pending: 'text-text-primary',
  in_progress: 'text-text-primary',
  completed: 'text-text-secondary line-through',
  cancelled: 'text-text-tertiary line-through',
};

// Every indicator stays mounted so a status change cross-fades.
const PlanStepIndicator = ({ status }: { status: AgentTodoItemStatus }): JSX.Element => (
  <span className="relative size-3.5 shrink-0">
    {STATUS_INDICATORS.map(([candidate, Icon, tone]) => (
      <Icon
        key={candidate}
        className={cn(
          'absolute inset-0 size-3.5 motion-safe:transition-opacity motion-safe:duration-150 motion-safe:ease-out-quart',
          tone,
          candidate === status ? 'opacity-100' : 'opacity-0'
        )}
      />
    ))}
  </span>
);

// `textClass` sets the row's font size, which the marker's `1lh` centres on.
const RailRow = ({
  marker,
  isLast,
  textClass,
  children,
  ...rest
}: {
  marker: ReactNode;
  isLast: boolean;
  textClass: string;
  children: ReactNode;
  'data-testid'?: string;
  'data-status'?: AgentTodoItemStatus;
}): JSX.Element => (
  <li className={cn('flex gap-5 leading-normal', textClass)} {...rest}>
    <span className="flex w-4 shrink-0 flex-col items-center" aria-hidden>
      <span className="flex h-[1lh] items-center">{marker}</span>
      {!isLast ? <span className="w-px flex-1 bg-border" /> : null}
    </span>
    <div className={cn('min-w-0 flex-1', !isLast && 'pb-3')}>{children}</div>
  </li>
);

export interface PlanStepsPanelProps {
  /** Already staggered by the caller, which may also read the settled list. */
  items: readonly AgentTodoItem[];
  /**
   * Fewest leading completed steps that fold behind the summary row;
   * `PLAN_COLLAPSE_DISABLED` disables folding.
   */
  collapseAfter?: number;
  /** Rows shown before a "Show more" toggle takes over. */
  disclosureLimit?: number;
}

/**
 * The plan's step rail with its own completed-head fold — shared by the plan
 * cards, the pinned summary and the resident drawer.
 */
export const PlanStepsPanel = ({
  items,
  collapseAfter,
  disclosureLimit,
}: PlanStepsPanelProps): JSX.Element => {
  const intl = useIntl();
  // The steps ARE the plan, so they share the card title's `body` rung and move
  // with the thread's density. A host that is not the thread publishes its own.
  const { body } = useResponseTypeClasses();
  const [foldExpanded, setFoldExpanded] = useState(false);
  const [showAll, setShowAll] = useState(false);

  // A `todo set` can replace the plan without remounting the panel, so reset
  // the replacement rather than inherit its expansion; status ticks don't count.
  const planSignature = items.map((item) => `${item.id}:${item.title}`).join('|');
  const previousSignature = useRef(planSignature);
  if (previousSignature.current !== planSignature) {
    previousSignature.current = planSignature;
    setFoldExpanded(false);
    setShowAll(false);
  }

  // Fold the staggered list, never the raw one: the tick animation walks
  // positions in the plan's own order, which a pre-folded list would renumber.
  const { collapsedCount, visibleItems } = collapsePlanSteps(items, collapseAfter);
  const unfolded = foldExpanded ? items : visibleItems;
  const hiddenCount = disclosureLimit
    ? Math.max(unfolded.length - disclosureLimit, 0)
    : 0;
  const steps =
    hiddenCount > 0 && !showAll ? unfolded.slice(0, disclosureLimit) : unfolded;

  return (
    <div className="flex w-full flex-col gap-2" data-testid="plan-steps-panel">
      <ol className="flex flex-col">
        {collapsedCount > 0 ? (
          <RailRow
            marker={<Check className="size-3.5 text-text-secondary" />}
            isLast={steps.length === 0}
            textClass="text-sm"
          >
            <Button
              variant="ghost"
              size="xs"
              aria-expanded={foldExpanded}
              className="h-auto w-fit justify-start gap-2 p-0 text-sm font-normal leading-normal text-text-secondary hover:bg-transparent hover:text-text-primary"
              onClick={() => setFoldExpanded((previous) => !previous)}
              data-testid="plan-card-collapsed-steps"
            >
              {intl.formatMessage(agentTodoMessages.planStepsDone, {
                count: collapsedCount,
              })}
              <ChevronDown
                className={cn(
                  'size-3.5 shrink-0 motion-safe:transition-transform motion-safe:duration-200 motion-safe:ease-out-quart',
                  foldExpanded && 'rotate-180'
                )}
                aria-hidden
              />
            </Button>
          </RailRow>
        ) : null}
        {steps.map((item, index) => (
          <RailRow
            key={item.id}
            marker={<PlanStepIndicator status={item.status} />}
            isLast={index === steps.length - 1}
            textClass={body}
            data-testid="plan-card-step"
            data-status={item.status}
          >
            <TypographyP2
              className={cn(
                '[overflow-wrap:anywhere] motion-safe:transition-colors motion-safe:duration-150 motion-safe:ease-out-quart',
                LABEL_CLASS[item.status],
                body
              )}
            >
              {item.title}
            </TypographyP2>
            {item.notes ? (
              <TypographyP2
                className={cn('text-text-tertiary [overflow-wrap:anywhere]', body)}
              >
                {item.notes}
              </TypographyP2>
            ) : null}
          </RailRow>
        ))}
      </ol>
      {hiddenCount > 0 ? (
        <Button
          variant="ghost"
          size="sm"
          aria-expanded={showAll}
          className="h-auto w-fit justify-start gap-5 p-0 font-normal text-text-secondary hover:bg-transparent hover:text-text-primary"
          onClick={() => setShowAll((previous) => !previous)}
        >
          {showAll ? (
            <ChevronsUp className="size-4 shrink-0" aria-hidden />
          ) : (
            <ChevronsUpDown className="size-4 shrink-0" aria-hidden />
          )}
          {intl.formatMessage(
            showAll
              ? agentTodoMessages.showFewerPlanSteps
              : agentTodoMessages.showMorePlanSteps,
            { count: hiddenCount }
          )}
        </Button>
      ) : null}
    </div>
  );
};
